import React, { useState, useEffect, useRef } from 'react';
import { TableCell, TextField, Stack, InputAdornment, Typography, Tooltip } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms/States';
import InfoButton from './InfoButton';
import EditInfoTrigger from './triggers/EditInfoTrigger';
import ResetButton from './ResetButton';
import CompareNumber from './CompareNumber';
import { EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES } from '../../../constants/forecast';
import { useRollingWeeksLabel } from '../../../hooks/Forecast/useRollingWeeksLabel';
import { getCellSourceTooltip } from '../utils/forecastSourceTooltip';

/**
 * EditableCell - Renders an editable cell in the forecast table
 *
 * Props:
 * - mapKey: string - unique key for this cell (forecastId_weekDay_zoneId)
 * - cellData: object - current cell data from editingDataMap
 * - initialCellData: object - initial cell data for comparison/reset
 * - isChanged: boolean - whether this cell has unsaved changes
 * - highlightColor: string - background color for changed cells
 * - isAutoMode: boolean - whether the cell's day column is auto-calculated (AUTO off → manual forecast tooltip)
 * - timezone: string|null - selected region's timezone, used to format the source date tooltip
 * - onDataChange: (mapKey, updates) => void - callback for value changes
 * - onOpenEditInfo: () => void - callback to open edit info modal
 */
const EditableCell = ({ mapKey, cellData, initialCellData, isChanged, highlightColor, isAutoMode, timezone, onDataChange, onOpenEditInfo }) => {
  const user = useRecoilValue(userState);
  const rollingWeeksLabel = useRollingWeeksLabel();
  const currentValue = cellData?.average_ticket ?? '';
  const [value, setValue] = useState(currentValue);
  const isEditingRef = useRef(false);
  const [isValueHovered, setIsValueHovered] = useState(false); // source tooltip opens only over the value, not the endAdornment
  const [isFocused, setIsFocused] = useState(false); // don't show tooltip while the user is editing the value

  // Update local state when cellData changes (e.g., after reset or data reload)
  // But only if not currently editing
  useEffect(() => {
    if (!isEditingRef.current) {
      setValue(currentValue);
    }
  }, [currentValue]);

  const handleChange = (e) => {
    isEditingRef.current = true;
    const inputValue = e.target.value;

    if (inputValue === '' || Number(inputValue) >= 0) {
      setValue(inputValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && Number(value) <= 0) {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    isEditingRef.current = false;
    const newValue = value === '' ? '' : Number(value);

    if (onDataChange && newValue !== currentValue) {
      onDataChange(mapKey, { average_ticket: newValue });
    }
  };

  const handleReset = () => {
    if (!initialCellData || !onDataChange || !mapKey) return;

    // Reset average_ticket to to_reset_tickets value
    const initialAvgTicket = initialCellData.to_reset_tickets;
    setValue(initialAvgTicket);
    onDataChange(mapKey, { average_ticket: initialAvgTicket });
  };

  const hasEditableScope = EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES.length === 0 || user?.user?.scopes?.some((scope) => EDITABLE_WAREHOUSE_AND_INFO_DATA_SCOPES.includes(scope));
  const disableResetButton = [null, undefined, ''].includes(initialCellData?.to_reset_tickets) || Number(value) === initialCellData?.to_reset_tickets;

  if (!cellData) {
    return <TableCell align="center">-</TableCell>;
  }

  // Source date only describes the system forecast - not a number edited by a user (saved or unsaved);
  const isSystemForecast = !initialCellData?.updated_by && !initialCellData?.isJustSavedByCurrentUser && !isChanged;
  const sourceTooltip = isSystemForecast ? getCellSourceTooltip({ isAutoMode, sourceDate: cellData.source_date, timezone }) : '';

  const HORIZONTAL_PADDING = 16;
  return (
    <TableCell
      sx={{
        bgcolor: isChanged ? highlightColor : 'transparent',
        position: 'relative',
        padding: `24px ${HORIZONTAL_PADDING}px`,
      }}
    >
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'start', alignItems: 'center' }}>
        <Tooltip title={sourceTooltip} open={isValueHovered && !isFocused && !!sourceTooltip} disableHoverListener disableFocusListener disableTouchListener>
          <TextField
            type="number"
            size="small"
            value={value}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            sx={{
              width: 120,
              // Hide number input spin buttons
              '& input[type=number]': {
                MozAppearance: 'textfield', // Firefox
              },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none', // Chrome, Safari, Edge
                margin: 0,
              },
            }}
            slotProps={{
              input: {
                step: 1,
                endAdornment: initialCellData ? (
                  <InputAdornment position="end">
                    <CompareNumber mainNumber={value} compareNumber={initialCellData.rolling_weeks} tooltipText={`Compared to ${rollingWeeksLabel}`} />
                  </InputAdornment>
                ) : null,
              },

              htmlInput: {
                min: 0,
                onMouseEnter: () => setIsValueHovered(true),
                onMouseLeave: () => setIsValueHovered(false),
              },
            }}
          />
        </Tooltip>
        {hasEditableScope ? <EditInfoTrigger onOpen={onOpenEditInfo} /> : <InfoButton data={cellData} />}
        <ResetButton disabled={disableResetButton} onClick={handleReset} />
      </Stack>
      {initialCellData?.updated_by && (
        <Typography
          as="div"
          variant="caption"
          sx={{
            fontStyle: 'italic',
            textAlign: 'left',
            mt: 0.5,
            position: 'absolute',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: `calc(100% - ${HORIZONTAL_PADDING * 2}px)`,
          }}
          color="textSecondary"
          title={initialCellData.updated_by}
        >
          Edited by {initialCellData.updated_by}
        </Typography>
      )}
    </TableCell>
  );
};

export default EditableCell;
